/**
 * Service for fetching environment settings from the API
 * This service handles the retrieval of client-specific environment settings
 * using the provided access key. It supports both production and staging environments.
 */
import axios from 'axios';

/**
 * Environment API endpoints
 */
const API_ENDPOINTS = {
    PRODUCTION: 'https://super-admin.recomdo.ai',
    STAGING: 'https://staging-super-admin.recomdo.ai'
};

/**
 * Fetch environment settings from the API using the provided access key
 *
 * @param {string} accessKey - Client-specific access key for authentication
 * @param {string} environment - Environment to use ('production' or 'staging')
 * @returns {Promise<Object|null>} - Environment settings object or null if error
 *
 * The returned environment settings object contains:
 * - REACT_APP_API_BASE_URL: Base URL for API calls
 * - REACT_APP_CLIENT_KEY: Client key for authentication
 * - REACT_APP_ACCESS_KEY: Access key for authentication
 * - REACT_APP_ACCESS_SECRET: Access secret for authentication
 */
const fetchEnvironmentSettings = async (shopUrl, accessKey, environment = 'production') => {
    if (!accessKey) {
        console.error('Access key is required to fetch environment settings');
        return null;
    }

    if (environment !== 'production' && environment !== 'staging') {
        console.warn(`Invalid environment "${environment}". Using "production" as default.`);
        environment = 'production';
    }

    const baseUrl = environment === 'production'
        ? API_ENDPOINTS.PRODUCTION
        : API_ENDPOINTS.STAGING;

    const getBootstrapBearer = () => {
        try {
            const cfgBearer = window.__VOICE_SEARCH_CONFIG__?.initialAuthorization
                || window.__VOICE_SEARCH_CONFIG__?.authorizationBearer
                || window.__VOICE_SEARCH_CONFIG__?.auth_token;
            if (cfgBearer && typeof cfgBearer === 'string') return cfgBearer;
            if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENV_BOOTSTRAP_BEARER) {
                return process.env.REACT_APP_ENV_BOOTSTRAP_BEARER;
            }
        } catch (_) {
        }
        return null;
    };

    const tryGenerateAccessToken = async () => {
        const bootstrapBearer = getBootstrapBearer();
        try {
            const headers = {accept: 'application/json'};
            if (bootstrapBearer) {
                headers['Authorization'] = `Bearer ${bootstrapBearer}`;
            }
            const tokenResp = await axios.post(
                `${baseUrl}/billing/recomdoai_api/generate_access_token?access_key=${encodeURIComponent(accessKey)}`,
                {},
                {headers}
            );
            if (tokenResp.status === 200 && tokenResp.data?.token) {
                return tokenResp.data.token;
            }
            console.warn('Token generation did not return a token.');
            try {
                if (typeof window !== 'undefined') window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
            } catch (_) {
            }
            return null;
        } catch (e) {
            if (e?.response) {
                console.warn(`Token generation failed: ${e.response.status} ${e.response.statusText}`);
            } else {
                console.warn('Token generation failed:', e?.message || e);
            }
            try {
                if (typeof window !== 'undefined') window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
            } catch (_) {
            }
            return null;
        }
    };

    try {

        let response;
        const generatedToken = await tryGenerateAccessToken();

        if (!generatedToken) {
            console.error('Token generation did not provide a token. Aborting environment fetch.');
            try {
                if (typeof window !== 'undefined') window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
            } catch (_) {
            }
            return null;
        }

        let extensionBaseUrl = null;
        let apiBaseUrl = null;
        try {
            if (typeof window !== 'undefined') {
                extensionBaseUrl = `https://${shopUrl}`;
                apiBaseUrl =  window.__VOICE_SEARCH_CONFIG__?.url;
                if (extensionBaseUrl && !extensionBaseUrl.endsWith('/')) {
                    extensionBaseUrl = `${extensionBaseUrl}/`;
                }
            }
        } catch (_) {
        }

        response = await axios.get(
            `${baseUrl}/billing/recomdoai_api/get_environment_by_access_key`,
            {
                headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${generatedToken}`
                },
                params: (extensionBaseUrl || apiBaseUrl) ? { ...(extensionBaseUrl ? { extension_base_url: extensionBaseUrl } : {}), ...(apiBaseUrl ? { api_base_url: apiBaseUrl } : {}) } : undefined
            }
        );

        if (response.status === 200 && response.data?.message === "Environment data found") {
            let decoded;
            try {
                const rawData = response.data?.data;
                if (typeof rawData === 'string') {
                    const decodeBase64 = (s) => {
                        try {
                            if (typeof atob === 'function') {
                                return decodeURIComponent(escape(atob(s)));
                            }
                        } catch (_) {
                        }
                        try {
                            return Buffer.from(s, 'base64').toString('utf-8');
                        } catch (e) {
                            throw new Error('Base64 decode failed');
                        }
                    };
                    const jsonStr = decodeBase64(rawData);
                    decoded = JSON.parse(jsonStr);
                } else if (rawData && typeof rawData === 'object') {
                    decoded = rawData;
                } else {
                    decoded = null;
                }
            } catch (err) {
                console.error('Failed to decode environment data from API response:', err?.message || err);
                try {
                    if (typeof window !== 'undefined') window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
                } catch (_) {
                }
                return null;
            }

            if (!decoded?.environment) {
                console.error('Environment data not found in API response');
                try {
                    if (typeof window !== 'undefined') window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
                } catch (_) {
                }
                return null;
            }

            const envData = decoded.environment;

            if (!envData.api_base_url) {
                console.error('API base URL not found in environment data');
                try {
                    if (typeof window !== 'undefined') window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
                } catch (_) {
                }
                return null;
            }

            const apiBaseUrl = window.__VOICE_SEARCH_CONFIG__?.url
                ? `${window.__VOICE_SEARCH_CONFIG__.url}/search/recomdoai_api`
                : `${envData.api_base_url}/search/recomdoai_api`;
            sessionStorage.setItem("REACT_APP_CLIENT_KEY", envData.client_key || '',);
            return {
                REACT_APP_API_BASE_URL: apiBaseUrl,
                REACT_APP_CLIENT_KEY: envData.client_key || '',
                REACT_APP_ACCESS_KEY: envData?.access_key || '',
                REACT_APP_ACCESS_SECRET: envData?.access_secret || '',
                PROFILE_IMAGE_URL: envData.profile_image_url || '',
                CAROUSEL_DATA: envData.carousel_data || '',
                TRANSCRIPTION_SERVICE: envData.transcription_service || '',
                PHRASES: envData.phrases || '',
                TRIAL_PERIOD: envData.is_trial || '',
                is_use_wake_word: (typeof envData.is_use_wake_word === 'boolean') ? envData.is_use_wake_word : false,
                is_use_pick_slider: (typeof envData.is_use_pick_slider === 'boolean') ? envData.is_use_pick_slider : true,
                plugin_platform:envData.plugin_platform || ''
            };
        }

        console.error('Invalid response from environment API:',
            response.data?.message || 'No message provided',
            'Status:', response.status);
        try {
            if (typeof window !== 'undefined') window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
        } catch (_) {
        }
        return null;
    } catch (error) {
        if (error.response) {
            console.error(`Error fetching environment settings: ${error.response.status} ${error.response.statusText}`);
            console.error('Error details:', error.response.data);
            try {
                if (typeof window !== 'undefined' && error.response.status === 401) {
                    window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
                }
            } catch (_) {
            }
        } else if (error.request) {
            console.error('Error fetching environment settings: No response received from server');
            try {
                if (typeof window !== 'undefined') window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
            } catch (_) {
            }
        } else {
            console.error('Error fetching environment settings:', error.message);
            try {
                if (typeof window !== 'undefined') window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
            } catch (_) {
            }
        }
        return null;
    }
};

export {fetchEnvironmentSettings};
